import { money } from "../../Subscription/money";
import { taxLinesOf, taxLinesPhrase } from "../../Subscription/taxLines";

/**
 * How the CRM shows an office's plan state — the same gate the app applies
 * (usage.services.access.office_plan_gate): subscribed / exempt / trial / open /
 * blocked (trial_exhausted | no_plan). Pure, for tests.
 */

const STATES = {
  subscribed: { label: "Paid plan", cls: "is-subscribed" },
  exempt: { label: "Complimentary", cls: "is-exempt" },
  trial: { label: "Free trial", cls: "is-trial" },
  open: { label: "No plan", cls: "is-open" },
  blocked: { label: "Needs a plan", cls: "is-blocked" },
};

export function planStatePill(row) {
  const state = (row && row.state) || "open";
  const base = STATES[state] || STATES.open;
  if (state === "blocked" && row.reason === "trial_exhausted") return { ...base, label: "Trial used up" };
  return base;
}

/** One sentence under the plan: what the office's users experience right now. */
export function planStateSentence(row) {
  if (!row) return "";
  const t = row.trial || {};
  switch (row.state) {
    case "subscribed":
      return "";
    case "exempt":
      return "Complimentary access — this office uses Smilepass without a plan and is never charged.";
    case "trial":
      return t.active
        ? `On a free trial — ${t.used || 0} of ${t.quota || 0} requests used. They can choose a plan any time in the app.`
        : "A free trial starts on their first request. They can choose a plan any time in the app.";
    case "blocked":
      return row.reason === "trial_exhausted"
        ? "The free trial is used up. Product pages ask them to choose a plan until one is bought."
        : "No plan and no free trial. Product pages ask them to choose a plan until one is bought.";
    default:
      return "No plan. Plans aren't required yet (CRM Settings → Plans & trials), so the office uses Smilepass as before.";
  }
}

/** "Oct 31, 2026" from an ISO date or datetime; "—" when missing. */
export function shortDate(iso) {
  if (!iso) return "—";
  const [y, m, d] = String(iso).slice(0, 10).split("-").map(Number);
  if (!y || !m || !d) return "—";
  return new Date(y, m - 1, d).toLocaleDateString("en-CA", { month: "short", day: "numeric", year: "numeric" });
}

/**
 * A purchase's charge for display, from its own price snapshot:
 * {total: "$3,797.52", list: "$4,788" | null, note: "Includes Founding offer: −$600" | null,
 *  tax: "HST $436.87" | "GST $190.40 + PST $266.56" | ""} — the tax it was bought with.
 */
export function chargeView(charge) {
  if (!charge) return null;
  const discount = Number(charge.discount || 0);
  const label = charge.offer && charge.offer.label;
  return {
    total: `$${money(charge.total)}`,
    subtotal: `$${money(charge.subtotal)}`,
    list: discount > 0 ? `$${money(charge.list_subtotal)}` : null,
    note: discount > 0 ? `${label || "Special offer"}: −$${money(charge.discount)} before tax` : null,
    tax: taxLinesPhrase(taxLinesOf(charge)),
  };
}

/** "Pro (Annual) · active" / "Starter (Monthly) · awaiting payment". */
export function planLine(plan) {
  if (!plan) return "—";
  const status = plan.status === "pending" ? "awaiting payment" : plan.status;
  return status ? `${plan.tier_title || "Plan"} · ${status}` : plan.tier_title || "Plan";
}
